import type {PromotionCenterInfoStoreState} from './types';

import {Divider, Loading} from '@ds/components';
import {PageLayout} from '@ds/layout';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {routes} from 'routes/routes';
import {useAppDispatch, useAppSelector} from 'store';
import {getCycle, getDiscountFilters} from 'store/slices/promotion-center/thunk';
import {getSelectedVendorFromStorage} from 'utils/Helper';

import ActiveDiscount from './components/ActiveDiscount/ActiveDiscount';
import AvailableDiscounts from './components/AvailableDiscounts/AvailableDiscounts';
import CampaignCountdown from './components/CampaignCountdown/CampaignCountdown';
import PromotionHistory from './components/PromotionHistory/PromotionHistory';
import QueuedDiscounts from './components/QueuedDiscounts/QueuedDiscounts';
import {normalizeCycleGroups, normalizeCycleHistory} from './utils';

const PROMOTION_HISTORY_PAGE_SIZE = 10;

const PromotionCenterInfo: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [historyPage, setHistoryPage] = useState(1);
  const isLoadingMoreHistory = useRef(false);
  const promotionCenterState = useAppSelector(
    (state) => state.promotionCenter
  ) as PromotionCenterInfoStoreState;
  const cycleGroups = normalizeCycleGroups(promotionCenterState.cycleData);
  const cycleHistory = normalizeCycleHistory(promotionCenterState.cycleHistory);
  const selectedVendor = getSelectedVendorFromStorage();
  const vendorId =
    typeof selectedVendor?.id === 'string' || typeof selectedVendor?.id === 'number'
      ? selectedVendor.id
      : '0';

  useEffect(() => {
    setHistoryPage(1);
    isLoadingMoreHistory.current = false;
    void dispatch(getCycle({vendorId}));
    void dispatch(
      getDiscountFilters({
        vid: vendorId,
        page: 1,
        page_size: PROMOTION_HISTORY_PAGE_SIZE
      })
    );
  }, [dispatch, vendorId]);

  const navigateToDiscount = useCallback(
    (cycleId: string | number) => {
      navigate(routes.PROMOTION_CENTER_DISCOUNT_VIEW.replace(':cycleId', String(cycleId)));
    },
    [navigate]
  );

  const loadMoreHistory = useCallback(() => {
    if (isLoadingMoreHistory.current) {
      return;
    }

    isLoadingMoreHistory.current = true;
    const nextPage = historyPage + 1;
    void dispatch(
      getDiscountFilters({
        vid: vendorId,
        page: nextPage,
        page_size: PROMOTION_HISTORY_PAGE_SIZE
      })
    )
      .unwrap()
      .then(
        () => setHistoryPage(nextPage),
        () => undefined
      )
      .finally(() => {
        isLoadingMoreHistory.current = false;
      });
  }, [dispatch, historyPage, vendorId]);

  if (promotionCenterState.loading > 0) {
    return (
      <div className="flex min-h-60 flex-1 items-center justify-center">
        <Loading size="large" color="primary" />
      </div>
    );
  }

  const thresholdCycle = cycleGroups.threshold?.[0];
  const liveCycle = cycleGroups.in_ddc?.[0];

  return (
    <PageLayout contentId="promotion-center-info">
      {thresholdCycle && (
        <>
          <CampaignCountdown date={thresholdCycle.deal_start_date} />
          <Divider className="bg-gray-02" size={8} />
        </>
      )}

      {cycleGroups.in_dsc && cycleGroups.in_dsc.length > 0 && (
        <AvailableDiscounts cycles={cycleGroups.in_dsc} />
      )}

      {liveCycle && (
        <>
          <ActiveDiscount
            cycle={liveCycle}
            onClick={() => navigateToDiscount(liveCycle.cycle_id)}
          />
          <Divider className="bg-gray-02" size={8} />
        </>
      )}

      {cycleGroups.is_combined && cycleGroups.is_combined.length > 0 && (
        <>
          <QueuedDiscounts cycles={cycleGroups.is_combined} onItemClick={navigateToDiscount} />
          <Divider className="bg-gray-02" size={8} />
        </>
      )}

      <PromotionHistory
        history={cycleHistory}
        onLoadMore={loadMoreHistory}
        onItemClick={navigateToDiscount}
      />
    </PageLayout>
  );
};

export default PromotionCenterInfo;
